<template>
  <div class="modern-master-view pb-12">
    <v-row class="mb-8 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="48" class="mr-4 shadow-sm">
          <v-icon color="white">fas fa-university</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text lh-1">Tesorería</div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">
            Cuentas, caja y movimientos · cobros y pagos de Finance
          </div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-btn outlined color="primary" class="font-weight-black text-uppercase tracking-widest rounded-lg px-6" :loading="loading" @click="fetchAll">
        <v-icon left small>fas fa-redo</v-icon> Actualizar
      </v-btn>
    </v-row>

    <v-row class="mb-6 px-4">
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="modern-card pa-6">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Saldo tesorería</div>
          <div class="text-h5 font-weight-black primary--text">{{ summary.total_balance | moneda }}</div>
          <div class="caption grey--text">{{ summary.accounts_count || 0 }} cuentas activas</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="modern-card pa-6">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Ingresos del mes</div>
          <div class="text-h5 font-weight-black success--text">{{ summary.inflow_month | moneda }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="modern-card pa-6">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Egresos del mes</div>
          <div class="text-h5 font-weight-black error--text">{{ summary.outflow_month | moneda }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="modern-card pa-6">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Sin conciliar</div>
          <div class="text-h5 font-weight-black">{{ summary.unmatched_movements || 0 }}</div>
        </v-card>
      </v-col>
    </v-row>

    <v-card outlined class="modern-card mx-4">
      <v-tabs v-model="tab" background-color="transparent">
        <v-tab>Cuentas</v-tab>
        <v-tab>Movimientos</v-tab>
      </v-tabs>
      <v-tabs-items v-model="tab">
        <v-tab-item>
          <div class="pa-4 d-flex justify-end">
            <v-btn color="primary" class="font-weight-bold" @click="openAccount">
              <v-icon left small>fas fa-plus</v-icon> Nueva cuenta
            </v-btn>
          </div>
          <v-data-table :headers="accountHeaders" :items="accounts" :loading="loading" hide-default-footer class="elevation-0" no-data-text="Cree una cuenta bancaria o caja.">
            <template v-slot:item.current_balance="{ item }">{{ item.current_balance | moneda }}</template>
            <template v-slot:item.opening_balance="{ item }">{{ item.opening_balance | moneda }}</template>
            <template v-slot:item.is_default="{ item }">
              <v-chip v-if="item.is_default" x-small color="primary" class="white--text">Default</v-chip>
            </template>
            <template v-slot:item.is_active="{ item }">
              <v-chip x-small :color="item.is_active ? 'success' : 'grey'" class="white--text">{{ item.is_active ? 'Activa' : 'Inactiva' }}</v-chip>
            </template>
          </v-data-table>
        </v-tab-item>
        <v-tab-item>
          <div class="pa-4 d-flex align-center" style="gap: 12px;">
            <v-select v-model="accountFilter" :items="accountItems" item-text="text" item-value="value" outlined dense hide-details clearable label="Cuenta" class="modern-input" style="max-width: 280px;" @change="fetchMovements" />
            <v-spacer />
            <v-btn outlined color="primary" @click="openTransfer">Transferir</v-btn>
            <v-btn color="primary" class="font-weight-bold" @click="openMovement">Nuevo movimiento</v-btn>
          </div>
          <v-data-table :headers="movementHeaders" :items="movements" :loading="loadingMov" hide-default-footer class="elevation-0" no-data-text="Sin movimientos.">
            <template v-slot:item.signed_amount="{ item }">
              <span :class="item.direction === 'in' ? 'success--text font-weight-black' : 'error--text font-weight-black'">
                {{ item.signed_amount | moneda }}
              </span>
            </template>
            <template v-slot:item.balance_after="{ item }">{{ item.balance_after | moneda }}</template>
            <template v-slot:item.reconciliation_status="{ item }">
              <v-chip x-small>{{ item.reconciliation_status_label }}</v-chip>
            </template>
          </v-data-table>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <v-dialog v-model="accDialog" max-width="480">
      <v-card>
        <v-card-title class="font-weight-black">Nueva cuenta</v-card-title>
        <v-card-text>
          <v-text-field v-model="acc.code" label="Código" outlined dense />
          <v-text-field v-model="acc.name" label="Nombre" outlined dense />
          <v-select v-model="acc.account_type" :items="typeItems" item-text="text" item-value="value" label="Tipo" outlined dense />
          <v-text-field v-model="acc.bank_name" label="Banco" outlined dense />
          <v-text-field v-model="acc.account_number" label="N° de cuenta" outlined dense />
          <v-text-field v-model="acc.opening_balance" type="number" label="Saldo inicial" outlined dense />
          <v-switch v-model="acc.is_default" label="Cuenta por defecto" hide-details />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="accDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="saving" @click="submitAccount">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="movDialog" max-width="480">
      <v-card>
        <v-card-title class="font-weight-black">Nuevo movimiento</v-card-title>
        <v-card-text>
          <v-select v-model="mov.bank_account_id" :items="accountItems" item-text="text" item-value="value" label="Cuenta" outlined dense />
          <v-select v-model="mov.direction" :items="directionItems" item-text="text" item-value="value" label="Dirección" outlined dense />
          <v-select v-model="mov.movement_type" :items="movementTypeItems" item-text="text" item-value="value" label="Tipo" outlined dense />
          <v-text-field v-model="mov.amount" type="number" label="Monto" outlined dense />
          <v-text-field v-model="mov.movement_date" type="date" label="Fecha" outlined dense />
          <v-text-field v-model="mov.description" label="Descripción" outlined dense />
          <v-text-field v-model="mov.reference" label="Referencia" outlined dense />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="movDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="saving" @click="submitMovement">Registrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="xferDialog" max-width="480">
      <v-card>
        <v-card-title class="font-weight-black">Transferencia entre cuentas</v-card-title>
        <v-card-text>
          <v-select v-model="xfer.from_account_id" :items="accountItems" item-text="text" item-value="value" label="Origen" outlined dense />
          <v-select v-model="xfer.to_account_id" :items="accountItems" item-text="text" item-value="value" label="Destino" outlined dense />
          <v-text-field v-model="xfer.amount" type="number" label="Monto" outlined dense />
          <v-text-field v-model="xfer.movement_date" type="date" label="Fecha" outlined dense />
          <v-text-field v-model="xfer.reference" label="Referencia" outlined dense />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="xferDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="saving" @click="submitTransfer">Transferir</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import notifications from '@/utils/notifications'
import {
  createTreasuryAccount,
  createTreasuryMovement,
  createTreasuryTransfer,
  fetchTreasuryAccounts,
  fetchTreasuryMovements,
  fetchTreasurySummary,
} from '@/api/finance'

export default {
  name: 'FinanceTreasury',
  data() {
    return {
      tab: 0,
      loading: false,
      loadingMov: false,
      saving: false,
      summary: {},
      accounts: [],
      movements: [],
      accountFilter: '',
      accDialog: false,
      movDialog: false,
      xferDialog: false,
      acc: { code: '', name: '', account_type: 'bank', bank_name: '', account_number: '', opening_balance: 0, is_default: false },
      mov: { bank_account_id: '', direction: 'in', movement_type: 'deposit', amount: '', movement_date: '', description: '', reference: '' },
      xfer: { from_account_id: '', to_account_id: '', amount: '', movement_date: '', reference: '' },
      accountHeaders: [
        { text: 'Código', value: 'code' },
        { text: 'Nombre', value: 'name' },
        { text: 'Tipo', value: 'account_type_label' },
        { text: 'Banco', value: 'bank_name' },
        { text: 'N°', value: 'account_number' },
        { text: 'Apertura', value: 'opening_balance', align: 'end' },
        { text: 'Saldo', value: 'current_balance', align: 'end' },
        { text: '', value: 'is_default' },
        { text: 'Estado', value: 'is_active' },
      ],
      movementHeaders: [
        { text: 'Fecha', value: 'movement_date' },
        { text: 'Cuenta', value: 'account_code' },
        { text: 'Tipo', value: 'movement_type_label' },
        { text: 'Descripción', value: 'description' },
        { text: 'Monto', value: 'signed_amount', align: 'end' },
        { text: 'Saldo', value: 'balance_after', align: 'end' },
        { text: 'Conciliación', value: 'reconciliation_status' },
      ],
    }
  },
  computed: {
    accountItems() {
      return this.accounts.map(acc => ({ value: acc.id, text: `${acc.code} · ${acc.name}` }))
    },
    typeItems() {
      const labels = this.summary.account_types || { bank: 'Cuenta bancaria', cash: 'Caja', other: 'Otra' }
      return Object.keys(labels).map(value => ({ value, text: labels[value] }))
    },
    directionItems() {
      const labels = this.summary.directions || { in: 'Ingreso', out: 'Egreso' }
      return Object.keys(labels).map(value => ({ value, text: labels[value] }))
    },
    movementTypeItems() {
      const labels = this.summary.movement_types || { deposit: 'Depósito', withdrawal: 'Retiro' }
      return Object.keys(labels).map(value => ({ value, text: labels[value] }))
    },
  },
  methods: {
    today() {
      return new Date().toISOString().slice(0, 10)
    },
    async fetchAll() {
      this.loading = true
      try {
        const [summary, accounts] = await Promise.all([
          fetchTreasurySummary(),
          fetchTreasuryAccounts({ active_only: false }),
        ])
        this.summary = summary
        this.accounts = accounts.items || []
        await this.fetchMovements()
      } catch (e) {
        notifications.showError('No se pudo cargar tesorería')
      } finally {
        this.loading = false
      }
    },
    async fetchMovements() {
      this.loadingMov = true
      try {
        const data = await fetchTreasuryMovements({
          bank_account_id: this.accountFilter || undefined,
          page_size: 100,
        })
        this.movements = data.items || []
      } catch (e) {
        notifications.showError('No se pudieron cargar los movimientos')
      } finally {
        this.loadingMov = false
      }
    },
    openAccount() {
      this.acc = { code: '', name: '', account_type: 'bank', bank_name: '', account_number: '', opening_balance: 0, is_default: this.accounts.length === 0 }
      this.accDialog = true
    },
    async submitAccount() {
      this.saving = true
      try {
        await createTreasuryAccount(this.acc)
        notifications.showSuccess('Cuenta creada')
        this.accDialog = false
        await this.fetchAll()
      } catch (e) {
        notifications.showError((e.response && e.response.data && e.response.data.error) || 'No se pudo crear la cuenta')
      } finally {
        this.saving = false
      }
    },
    openMovement() {
      this.mov = {
        bank_account_id: this.accountFilter || (this.accounts[0] && this.accounts[0].id) || '',
        direction: 'in',
        movement_type: 'deposit',
        amount: '',
        movement_date: this.today(),
        description: '',
        reference: '',
      }
      this.movDialog = true
    },
    async submitMovement() {
      this.saving = true
      try {
        await createTreasuryMovement(this.mov)
        notifications.showSuccess('Movimiento registrado')
        this.movDialog = false
        await this.fetchAll()
      } catch (e) {
        notifications.showError((e.response && e.response.data && e.response.data.error) || 'No se pudo registrar')
      } finally {
        this.saving = false
      }
    },
    openTransfer() {
      this.xfer = {
        from_account_id: (this.accounts[0] && this.accounts[0].id) || '',
        to_account_id: (this.accounts[1] && this.accounts[1].id) || '',
        amount: '',
        movement_date: this.today(),
        reference: '',
      }
      this.xferDialog = true
    },
    async submitTransfer() {
      this.saving = true
      try {
        await createTreasuryTransfer(this.xfer)
        notifications.showSuccess('Transferencia registrada')
        this.xferDialog = false
        await this.fetchAll()
      } catch (e) {
        notifications.showError((e.response && e.response.data && e.response.data.error) || 'No se pudo transferir')
      } finally {
        this.saving = false
      }
    },
  },
  created() {
    this.fetchAll()
  },
}
</script>
